export const FLOW_MANIFEST_SCHEMA_VERSION = 1

export const STEP_VALIDATION_MODES = Object.freeze([
    'component',
    'none',
    'external',
])

export const STEP_COMPLETION_MODES = Object.freeze([
    'button',
    'external',
    'terminal',
])

export const TERMINAL_VALIDATION_SCOPES = Object.freeze([
    'all',
    'required',
    'none',
])

export const TERMINAL_INVALID_BEHAVIORS = Object.freeze([
    'stay',
    'review-summary',
    'open-first-invalid',
])

export const STEP_MOUNT_MODES = Object.freeze([
    'eager',
    'when-reachable',
])

export const UNAVAILABLE_BEHAVIORS = Object.freeze([
    'skip',
])

export const NAVIGATION_SCROLL_POLICIES = Object.freeze([
    'none',
    'start',
    'if-needed',
])

export const STEP_REVEAL_MODES = Object.freeze([
    'all',
    'progressive',
])

export const FLOW_PROGRESS_PERSISTENCE_MODES = Object.freeze([
    'blocking',
    'background',
])

export const FLOW_INITIAL_STEP_MODES = Object.freeze([
    'first',
    'resume',
])

export const FLOW_RENDERERS = Object.freeze([
    'vertical-collapse',
    'horizontal-stepper',
])

export const FLOW_CONTENT_CONTAINERS = Object.freeze([
    'card',
    'plain',
])

export const FLOW_STEPPER_SIZES = Object.freeze([
    'sm',
    'md',
])

export const FLOW_SAVE_STATUS_MODES = Object.freeze([
    'none',
    'header',
])

// This is deliberately data-only. The backend manifest DTO must be able to
// represent it without frontend component names, functions, or Vue references.
export const FLOW_MANIFEST_SCHEMA = Object.freeze({
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    additionalProperties: false,
    required: ['schemaVersion', 'id', 'version', 'settings', 'steps'],
    properties: {
        schemaVersion: {
            const: FLOW_MANIFEST_SCHEMA_VERSION,
        },
        id: {
            type: 'string',
            minLength: 1,
        },
        version: {
            type: 'integer',
            minimum: 1,
        },
        title: {
            type: 'string',
        },
        settings: {
            type: 'object',
            additionalProperties: false,
            required: ['accordion', 'showStepNumbers', 'revealMode'],
            properties: {
                renderer: {
                    enum: FLOW_RENDERERS,
                },
                rendererSettings: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        contentContainer: {
                            enum: FLOW_CONTENT_CONTAINERS,
                        },
                        showStepTitle: {
                            type: 'boolean',
                        },
                        showStepDescription: {
                            type: 'boolean',
                        },
                        showPreviousButton: {
                            type: 'boolean',
                        },
                        contentBordered: {
                            type: 'boolean',
                        },
                        stepperSize: {
                            enum: FLOW_STEPPER_SIZES,
                        },
                        saveStatus: {
                            enum: FLOW_SAVE_STATUS_MODES,
                        },
                        showSaveStatusText: {
                            type: 'boolean',
                        },
                    },
                },
                accordion: {
                    type: 'boolean',
                },
                showStepNumbers: {
                    type: 'boolean',
                },
                revealMode: {
                    enum: STEP_REVEAL_MODES,
                },
                nextStepOpenDelayMs: {
                    type: 'integer',
                    minimum: 0,
                },
                scrollToNextStep: {
                    type: 'boolean',
                },
                nextStepScrollDelayMs: {
                    type: 'integer',
                    minimum: 0,
                },
                navigation: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        scrollOnOpen: {
                            enum: NAVIGATION_SCROLL_POLICIES,
                        },
                    },
                },
                progressPersistenceMode: {
                    enum: FLOW_PROGRESS_PERSISTENCE_MODES,
                },
                initialStepMode: {
                    enum: FLOW_INITIAL_STEP_MODES,
                },
            },
        },
        steps: {
            type: 'array',
            minItems: 1,
            items: {
                type: 'object',
                additionalProperties: false,
                required: [
                    'id',
                    'type',
                    'typeVersion',
                    'order',
                    'enabled',
                    'titleOverride',
                    'numbered',
                    'availability',
                    'validation',
                    'completion',
                    'mount',
                    'navigation',
                ],
                properties: {
                    id: {
                        type: 'string',
                        minLength: 1,
                    },
                    type: {
                        type: 'string',
                        minLength: 1,
                    },
                    typeVersion: {
                        type: 'integer',
                        minimum: 1,
                    },
                    order: {
                        type: 'integer',
                    },
                    enabled: {
                        type: 'boolean',
                    },
                    titleOverride: {
                        type: ['string', 'null'],
                    },
                    navigationTitleOverride: {
                        type: ['string', 'null'],
                    },
                    descriptionOverride: {
                        type: ['string', 'null'],
                    },
                    numbered: {
                        type: 'boolean',
                    },
                    review: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['enabled'],
                        properties: {
                            enabled: {
                                type: 'boolean',
                            },
                            initiallyExpanded: {
                                type: 'boolean',
                            },
                            icon: {
                                type: 'string',
                            },
                        },
                    },
                    settings: {
                        type: 'object',
                    },
                    availability: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['visibleWhen', 'disabledWhen'],
                        properties: {
                            visibleWhen: {
                                type: ['string', 'null'],
                            },
                            disabledWhen: {
                                type: ['string', 'null'],
                            },
                        },
                    },
                    validation: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['mode'],
                        properties: {
                            mode: {
                                enum: STEP_VALIDATION_MODES,
                            },
                        },
                    },
                    completion: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['mode'],
                        properties: {
                            mode: {
                                enum: STEP_COMPLETION_MODES,
                            },
                            persistProgress: {
                                type: 'boolean',
                            },
                            requiredForTerminal: {
                                type: 'boolean',
                            },
                            validationScope: {
                                enum: TERMINAL_VALIDATION_SCOPES,
                            },
                            invalidBehavior: {
                                enum: TERMINAL_INVALID_BEHAVIORS,
                            },
                            scrollAfterExecution: {
                                type: 'boolean',
                            },
                        },
                    },
                    mount: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['mode'],
                        properties: {
                            mode: {
                                enum: STEP_MOUNT_MODES,
                            },
                        },
                    },
                    navigation: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['unavailableBehavior'],
                        properties: {
                            unavailableBehavior: {
                                enum: UNAVAILABLE_BEHAVIORS,
                            },
                            scrollOnOpen: {
                                enum: NAVIGATION_SCROLL_POLICIES,
                            },
                        },
                    },
                    legacy: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['persistedStep'],
                        properties: {
                            persistedStep: {
                                type: 'integer',
                                minimum: 1,
                            },
                        },
                    },
                },
            },
        },
    },
})
